import React, { useMemo, useState } from 'react';

import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { ExcludeDurationOption, TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import {
  FlaggedItemDTO,
  GetDailyWorkflowTaskSummaryDTO,
} from '@recurrency/core-api-schema/dist/dailyWorkflow/getDailyWorkflowTaskSummary';
import { Radio, Typography } from 'antd';
import { theme } from 'theme';

import { MultiUpdateReplenishmentSettingsAsyncTable } from 'pages/purchasing/PlanningPage/modals/MultiUpdateReplenishmentSettingsModal/MultiUpdateReplenishmentSettingsAsyncTable';
import { ReplenishmentMethod } from 'pages/purchasing/utils';

import { Button } from 'components/Button';
import { Counter } from 'components/Counter';
import { DividerLine } from 'components/DividerLine';
import { FlexSpace } from 'components/FlexSpace';
import { Modal } from 'components/Modal';
import { RadioGroup } from 'components/Radio/RadioGroup';

import { useCoreApi } from 'hooks/useApi';
import { useGlobalApp } from 'hooks/useGlobalApp';

import type { CloseableModalProps } from 'utils/asyncModal';
import { joinIfIdNameObj, joinObjectId } from 'utils/formatting';

import { ISODateStr } from '../../types/hash-state';
import { getTenantSetting } from '../../utils/tenantSettings';
import { ExcludeDurationSelector } from '../purchasing/PlanningPage/modals/ExcludeDurationSelector';
import { getIsoDateForExcludeDurationOption } from '../purchasing/PlanningPage/modals/utils';
import { DailyWorkflowProjectedInventoryCard } from './DailyWorkflowProjectedInventoryCard';
import { DailyWorkflowSummaryCard } from './DailyWorkflowSummaryCard';
import { getResolutionCounts } from './utils';

export type DailyWorkflowSubmissionConfirmationResults = {
  submitted: boolean;
  totalItems: number;
  accepted: number;
  overridden: number;
  flagged: number;
  bulkExcludeUntil?: ISODateStr;
  flaggedItems?: FlaggedItemDTO[];
};
type DailyWorkflowSubmissionConfirmationModalProps = CloseableModalProps<DailyWorkflowSubmissionConfirmationResults>;
type DailyWorkflowSubmissionConfirmationProps = DailyWorkflowSubmissionConfirmationModalProps & {
  summaryData?: GetDailyWorkflowTaskSummaryDTO;
  isLoading: boolean;
  hideSummaryCards?: boolean;
  headerContent?: React.ReactNode;
};

type PlanningWorkflowSubmissionConfirmationModalProps = DailyWorkflowSubmissionConfirmationProps;

export function DailyWorkflowSubmissionConfirmationModal({ onClose }: DailyWorkflowSubmissionConfirmationModalProps) {
  const { data: summaryData, isLoading } = useCoreApi(schemas.dailyWorkflow.getDailyWorkflowTaskSummary);
  return <DailyWorkflowSubmissionConfirmation onClose={onClose} summaryData={summaryData} isLoading={isLoading} />;
}

export function PlanningWorkflowSubmissionConfirmationModal({
  onClose,
  summaryData,
  isLoading,
  hideSummaryCards,
  headerContent,
}: PlanningWorkflowSubmissionConfirmationModalProps) {
  return (
    <DailyWorkflowSubmissionConfirmation
      onClose={onClose}
      summaryData={summaryData}
      isLoading={isLoading}
      hideSummaryCards={hideSummaryCards}
      headerContent={headerContent}
    />
  );
}

export function DailyWorkflowSubmissionConfirmation({
  onClose,
  summaryData,
  isLoading,
  hideSummaryCards,
  headerContent,
}: DailyWorkflowSubmissionConfirmationProps) {
  const { activeTenant } = useGlobalApp();
  const [currentPage, setCurrentPage] = useState(1);
  const [currentReplenishmentMethod, setCurrentReplenishmentMethod] = useState<ReplenishmentMethod | null>(
    ReplenishmentMethod.MinMax,
  );
  const defaultDuration =
    getTenantSetting(TenantSettingKey.FormExcludeDefaultDuration) ?? ExcludeDurationOption.OneMonth;
  const [selectedDuration, setSelectedDuration] = useState<ExcludeDurationOption>(defaultDuration);
  const [customDate, setCustomDate] = useState<ISODateStr | undefined>();
  const automatedItems = useMemo<Set<string>>(() => {
    if (!summaryData) {
      return new Set();
    }

    return new Set(
      summaryData?.acceptanceHistory.items
        .filter((item) => item.numConsecutiveAcceptances >= 2)
        .map((item) => joinObjectId([item.itemId, item.locationId])),
    );
  }, [summaryData]);

  const resolutionCounts = useMemo(() => getResolutionCounts(summaryData?.resolutionCounts), [summaryData]);

  const minMaxCount = useMemo(
    () =>
      summaryData?.modifiedMinMaxList.items.filter((item) => item.replenishmentMethod !== ReplenishmentMethod.OPOQ)
        .length ?? 0,
    [summaryData],
  );

  const opOqCount = useMemo(
    () =>
      summaryData?.modifiedMinMaxList.items.filter((item) => item.replenishmentMethod === ReplenishmentMethod.OPOQ)
        .length ?? 0,
    [summaryData],
  );

  const tableData = useMemo(() => {
    if (!summaryData) {
      return [];
    }
    const mappedTableData = summaryData.modifiedMinMaxList.items.map((item) => {
      const key = joinObjectId([item.itemId, item.locationId]);
      return {
        key,
        automated: automatedItems.has(key),
        locationDisplayName: joinIfIdNameObj({ foreignId: item.locationId, name: item.locationName ?? '' }),
        ...item,
      };
    });
    return currentReplenishmentMethod === ReplenishmentMethod.MinMax
      ? mappedTableData.filter((item) => item.replenishmentMethod !== ReplenishmentMethod.OPOQ)
      : mappedTableData.filter((item) => item.replenishmentMethod === ReplenishmentMethod.OPOQ);
  }, [automatedItems, summaryData, currentReplenishmentMethod]);

  const totalItemCount = resolutionCounts.accepted + resolutionCounts.overridden + resolutionCounts.flagged;

  const summaryCards = [
    {
      key: 'totalItems',
      value:
        resolutionCounts.accepted + resolutionCounts.overridden + resolutionCounts.flagged + resolutionCounts.excluded,
      label: 'Total Items',
      subLabel: 'Reviewed',
      color: theme.colors.neutral[800],
    },
    {
      key: 'accepted',
      value: resolutionCounts.accepted,
      label: 'Accepted',
      subLabel: '(Recurrency Values)',
      color: theme.colors.success[500],
    },
    {
      key: 'overridden',
      value: resolutionCounts.overridden,
      label: 'Accepted',
      subLabel: '(Custom Values)',
      color: theme.colors.info[700],
    },
    {
      key: 'excluded',
      value: resolutionCounts.excluded,
      label: 'Excluded',
      subLabel: 'Items',
      color: theme.colors.malibu[600],
    },
    {
      key: 'flagged',
      value: resolutionCounts.flagged,
      label: 'Flagged',
      subLabel: 'Items',
      color: theme.colors.danger[600],
    },
  ];

  const isShowingExclude = resolutionCounts.overridden > 0;
  const shouldExclude = isShowingExclude && selectedDuration !== ExcludeDurationOption.DontExclude;
  const excludedUntil =
    selectedDuration === ExcludeDurationOption.Custom
      ? customDate
      : getIsoDateForExcludeDurationOption(selectedDuration);

  const baseResults = {
    totalItems: totalItemCount,
    accepted: resolutionCounts.accepted,
    overridden: resolutionCounts.overridden,
    flagged: resolutionCounts.flagged,
    bulkExcludeUntil: shouldExclude ? excludedUntil : undefined,
    flaggedItems: summaryData?.flaggedItemList?.items ?? [],
  };

  return (
    <Modal
      visible
      title={`Updating Replenishment Settings for ${summaryData?.modifiedMinMaxList.totalCount} Items`}
      onCancel={() => onClose({ ...baseResults, submitted: false })}
      centered
      width="85%"
      bodyStyle={{ maxHeight: '85%' }}
      className={css`
        max-width: 1020px;
        min-width: 400px;
      `}
      footer={
        <>
          <Button key="cancel" onClick={() => onClose({ ...baseResults, submitted: false })} size="large">
            Cancel
          </Button>
          <Button
            key="Submit"
            type="primary"
            permissionsRequired={[
              TenantUserPermission.ActionExportMinMax,
              TenantUserPermission.ActionEditItemSupplierDetails,
            ]}
            htmlType="submit"
            size="large"
            onClick={() => onClose({ ...baseResults, submitted: true })}
          >
            Send to {activeTenant?.erpType?.toUpperCase() || 'ERP'}
          </Button>
        </>
      }
    >
      {!hideSummaryCards && (
        <FlexSpace
          direction="row"
          gap={32}
          fullWidth
          justify="space-between"
          className={css`
            margin-bottom: 32px;
          `}
        >
          {summaryCards.map(({ key, value, label, subLabel, color }) => (
            <DailyWorkflowSummaryCard
              key={key}
              value={value}
              label={label}
              subLabel={subLabel}
              color={color}
              loading={isLoading}
            />
          ))}
        </FlexSpace>
      )}

      {/* Render header content */}
      {headerContent}

      <DailyWorkflowProjectedInventoryCard minMaxUpdates={summaryData?.modifiedMinMaxList} />
      {isShowingExclude && (
        <ExcludeDurationSelector
          overrideClassName={css`
            margin-top: 0px;
          `}
          selectedDuration={selectedDuration}
          setSelectedDuration={setSelectedDuration}
          customDate={customDate}
          setCustomDate={setCustomDate}
          label="Exclude Accepted (Custom Min/Max Values) items for "
          includeDontExcludeOption
        />
      )}
      <DividerLine />
      <div style={{ marginBottom: 24 }}>
        <FlexSpace direction="row" gap={16} alignItems="center">
          <Typography.Title level={4} style={{ marginBottom: 16 }}>
            Settings to Change
          </Typography.Title>
          <FlexSpace
            className={css`
              margin-top: 0px;
              margin-bottom: 16px;
            `}
          >
            <RadioGroup
              value={currentReplenishmentMethod}
              onChange={(ev) => {
                setCurrentReplenishmentMethod(ev.target.value as ReplenishmentMethod);
                setCurrentPage(1);
              }}
            >
              <Radio.Button key={ReplenishmentMethod.MinMax} value={ReplenishmentMethod.MinMax}>
                {ReplenishmentMethod.MinMax}
                <Counter
                  className={css`
                    margin-left: 6px;
                  `}
                  variant="primary"
                  selected={currentReplenishmentMethod === ReplenishmentMethod.MinMax}
                  value={minMaxCount}
                />
              </Radio.Button>
              <Radio.Button key={ReplenishmentMethod.OPOQ} value={ReplenishmentMethod.OPOQ}>
                {ReplenishmentMethod.OPOQ}
                <Counter
                  className={css`
                    margin-left: 6px;
                  `}
                  variant="primary"
                  selected={currentReplenishmentMethod === ReplenishmentMethod.OPOQ}
                  value={opOqCount}
                />
              </Radio.Button>
            </RadioGroup>
          </FlexSpace>
        </FlexSpace>
        <MultiUpdateReplenishmentSettingsAsyncTable
          currentReplenishmentMethod={currentReplenishmentMethod}
          tableData={tableData}
          isLoading={isLoading}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </div>
    </Modal>
  );
}
